/** @file connectLive: a WebSocket live session on one puppet — hello, welcome, batches, commits. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Pose3DError } from '@zilo/pose3d-std/errors/pose3DError';
import type { LiveClientMessage, LiveServerMessage } from '../types/live';
import { buildUrl } from './buildUrl';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { makeError } from './makeError';
import type { WebSocketCtorLike, WebSocketLike } from './webTypes';

type Msg<T extends LiveServerMessage['type']> = Extract<LiveServerMessage, { type: T }>;

/** Callbacks of a session; `onCommitted` sees other actors' commits (own batches resolve `send`). */
export type LiveHandlers = {
  onWelcome?: (m: Msg<'welcome'>) => void;
  onCommitted?: (m: Msg<'committed'>) => void;
  onError?: (e: Pose3DError) => void;
  onClose?: (code: number) => void;
};

/** An open session: `send` resolves with this batch's `committed` or `rejected`; `revision()` tracks commits. */
export type LiveSession = {
  send: (commands: Command[], ifRevision?: number) => Promise<Msg<'committed' | 'rejected'>>;
  close: () => void;
  revision: () => number;
};

type Pending = { resolve(m: Msg<'committed' | 'rejected'>): void; reject(e: Error): void };

type State = {
  ws: WebSocketLike; where: string; handlers: LiveHandlers; revision: number; counter: number;
  closed: boolean; welcomed: boolean; pending: Map<string, Pending>;
  ready: { resolve(session: LiveSession): void; reject(e: Error): void };
};

const transport = (s: Pick<State, 'where'>, params: Record<string, unknown>, hint: string): Error =>
  makeError(ERROR_CODES.TRANSPORT, { url: s.where, ...params }, { hint });

function settle(s: State, requestId: string | undefined, m: Msg<'committed' | 'rejected'>): boolean {
  const p = requestId === undefined ? undefined : s.pending.get(requestId);
  if (p === undefined || requestId === undefined) return false;
  s.pending.delete(requestId);
  p.resolve(m);
  return true;
}

function sessionOf(s: State): LiveSession {
  const close = (): void => {
    if (s.closed) return;
    s.closed = true;
    s.ws.close(1000);
    rejectPending(s, 1000);
  };
  const send = (commands: Command[], ifRevision?: number): Promise<Msg<'committed' | 'rejected'>> => {
    if (s.closed) return Promise.reject(transport(s, {}, 'the live session is closed; connect again'));
    s.counter += 1;
    const message: LiveClientMessage = { type: 'batch', requestId: `r${String(s.counter)}`, commands, ...(ifRevision === undefined ? {} : { ifRevision }) };
    return new Promise((resolve, reject) => {
      s.pending.set(message.requestId, { resolve, reject });
      s.ws.send(JSON.stringify(message));
    });
  };
  return { send, close, revision: () => s.revision };
}

const ON: { [T in LiveServerMessage['type']]: (s: State, m: Msg<T>) => void } = {
  welcome: (s, m) => {
    s.revision = m.revision;
    s.welcomed = true;
    s.handlers.onWelcome?.(m);
    s.ready.resolve(sessionOf(s));
  },
  committed: (s, m) => {
    s.revision = m.revision;
    if (!settle(s, m.requestId, m)) s.handlers.onCommitted?.(m);
  },
  rejected: (s, m) => { settle(s, m.requestId, m); },
  error: (s, m) => {
    const e = makeError(m.error.code, { ...m.error.params }, { ...(m.error.path === undefined ? {} : { path: m.error.path }), ...(m.error.hint === undefined ? {} : { hint: m.error.hint }) });
    if (!s.welcomed) s.ready.reject(e);
    s.handlers.onError?.(e);
  },
  pong: () => undefined,
};

function parse(data: unknown): LiveServerMessage | null {
  if (typeof data !== 'string') return null;
  try {
    const m = JSON.parse(data) as { type?: unknown };
    return typeof m.type === 'string' && m.type in ON ? (m as LiveServerMessage) : null;
  } catch {
    return null;
  }
}

function onMessage(s: State, data: unknown): void {
  const m = parse(data);
  if (m === null) {
    s.handlers.onError?.(makeError(ERROR_CODES.TRANSPORT, { url: s.where }, { hint: 'unreadable live message' }));
    return;
  }
  (ON[m.type] as (s: State, m: LiveServerMessage) => void)(s, m);
}

function rejectPending(s: State, code: number): void {
  for (const p of s.pending.values()) p.reject(transport(s, { code }, 'the live socket closed before the batch was answered'));
  s.pending.clear();
}

function onClose(s: State, code: number): void {
  s.closed = true;
  rejectPending(s, code);
  if (!s.welcomed) s.ready.reject(transport(s, { code }, 'the live socket closed before welcome; check the token and its read scope'));
  s.handlers.onClose?.(code);
}

/** Opens `ws(s)://<host>/api/v1/puppets/<id>/live?token=…`, says hello as `actor`, resolves after welcome. */
export function connectLive(opts: ClientOptions, puppetId: string, actor: string, handlers: LiveHandlers, sinceRevision?: number): Promise<LiveSession> {
  const path = fillPath('/api/v1/puppets/{id}/live', { id: puppetId });
  const url = buildUrl(opts, { path, query: { token: opts.token } }).replace(/^http/, 'ws');
  const where = url.split('?')[0] ?? url;
  const Ctor = opts.WebSocket ?? (globalThis as { WebSocket?: WebSocketCtorLike }).WebSocket;
  if (Ctor === undefined) return Promise.reject(transport({ where }, {}, 'no global WebSocket: pass ClientOptions.WebSocket'));
  return new Promise((resolve, reject) => {
    const ws = new Ctor(url);
    const s: State = { ws, where, handlers, revision: 0, counter: 0, closed: false, welcomed: false, pending: new Map(), ready: { resolve, reject } };
    const hello: LiveClientMessage = { type: 'hello', actor, ...(sinceRevision === undefined ? {} : { sinceRevision }) };
    ws.addEventListener('open', () => { ws.send(JSON.stringify(hello)); });
    ws.addEventListener('message', (e) => { onMessage(s, e.data); });
    ws.addEventListener('close', (e) => { onClose(s, e.code ?? 1006); });
  });
}
